// MapDrawToolbar.jsx
// Drawing mode toggle buttons — milsymbol placement, geometry drawing, select/edit.

import React from 'react';
import {Stack, IconButton, Divider, Tooltip} from '@mui/material';
import EditRounded from '@mui/icons-material/EditRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import RedoRounded from '@mui/icons-material/RedoRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import MilitaryTechRounded from '@mui/icons-material/MilitaryTechRounded';

const ToolbarBtn = ({label, active, disabled, onClick, children}) => (
    <Tooltip title={label}>
        <span>
            <IconButton
                size="small"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={active}
                sx={(theme) => ({
                    borderRadius: 1,
                    p: 0.5,
                    color: active ? 'primary.main' : 'text.secondary',
                    bgcolor: active ? 'primary.50' : 'transparent',
                    border: active ? `1px solid ${theme.palette.primary.light}` : '1px solid transparent',
                    '&:hover': {bgcolor: active ? 'primary.100' : 'action.hover'},
                    '&.Mui-disabled': {opacity: 0.35},
                })}
            >
                {children}
            </IconButton>
        </span>
    </Tooltip>
);

// rerender-memo: parent (MapEditorModalInner) re-renders on every vertex placed while a
// tactical placement is in progress (event-driven placedCount updates) — mirrors ContextPanel's
// rationale. All props here are already stable (primitives, or useCallback-wrapped handlers).
const MapDrawToolbar = React.memo(function MapDrawToolbar({
    activeMode,
    onModeChange,
    onUndo,
    onRedo,
    onDeleteSelected,
    canUndo,
    canRedo,
    variant = 'header',
}) {
    const isCanvas = variant === 'canvas';

    const toggle = (mode) => onModeChange?.(activeMode === mode ? null : mode);

    return (
        <Stack
            direction={isCanvas ? 'column' : 'row'}
            alignItems="center"
            gap={0.5}
            role="toolbar"
            aria-label="Drawing tools"
            sx={(theme) => isCanvas ? {
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 3,
                p: 0.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: theme.shadows[2],
                border: `1px solid ${theme.palette.divider}`,
                pointerEvents: 'auto',
            } : {
                px: 1.5,
                py: 0.5,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
            }}
        >
            {/* Place military symbol */}
            <ToolbarBtn
                label="Place Military Symbol"
                active={activeMode === 'place-symbol'}
                onClick={() => toggle('place-symbol')}
            >
                <MilitaryTechRounded sx={{fontSize: 18}}/>
            </ToolbarBtn>

            <Divider orientation={isCanvas ? 'horizontal' : 'vertical'} flexItem sx={isCanvas ? {my: 0.5} : {mx: 0.5}}/>

            {/* Select / edit */}
            <ToolbarBtn
                label="Select / Edit"
                active={activeMode === 'select'}
                onClick={() => toggle('select')}
            >
                <EditRounded sx={{fontSize: 18}}/>
            </ToolbarBtn>

            <Divider orientation={isCanvas ? 'horizontal' : 'vertical'} flexItem sx={isCanvas ? {my: 0.5} : {mx: 0.5}}/>

            {/* Undo / redo / delete */}
            <Stack direction={isCanvas ? 'column' : 'row'} gap={0.25}>
                <ToolbarBtn label="Undo (Ctrl+Z)" disabled={!canUndo} onClick={onUndo}>
                    <UndoRounded sx={{fontSize: 18}}/>
                </ToolbarBtn>
                <ToolbarBtn label="Redo (Ctrl+Y)" disabled={!canRedo} onClick={onRedo}>
                    <RedoRounded sx={{fontSize: 18}}/>
                </ToolbarBtn>
                <Tooltip title="Delete selected">
                    <span>
                        <IconButton
                            size="small"
                            onClick={onDeleteSelected}
                            aria-label="Delete selected"
                            sx={{
                                borderRadius: 1,
                                p: 0.5,
                                color: 'error.main',
                                '&:hover': {bgcolor: 'error.50'},
                            }}
                        >
                            <DeleteOutlineRounded sx={{fontSize: 18}}/>
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Stack>
    );
});

export default MapDrawToolbar;
